import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import soundTherapyTracks from "./SmartBiometricTherapy.json";
import * as Icons from "@mui/icons-material";
import { getSmartBiometricSound } from "../../service/home";

const SmartBiometricTherapyDialog = ({ open, onClose, mode = 'manual' }) => {
    const user = JSON.parse(localStorage.getItem("user"));
    const [step, setStep] = useState(1);
    const [pulseCount, setPulseCount] = useState("");
    const [bpm, setBpm] = useState(null);
    const [answers, setAnswers] = useState({});
    const [showContinue, setShowContinue] = useState(false);
    const [selectedTracks, setSelectedTracks] = useState([]);
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isAudioCompleted, setIsAudioCompleted] = useState(false);
    const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
    const [duration, setDuration] = useState(0);       // total seconds
    const [currentTime, setCurrentTime] = useState(0); // played seconds
    const [needsUserPlay, setNeedsUserPlay] = useState(false); // mobile autoplay fallback
    const [isPlaying, setIsPlaying] = useState(false); // true only while audio is actually playing


    console.log("bpm", bpm)

    // Reset dialog when closed
    const handleClose = () => {
        setStep(1);
        setPulseCount("");
        setBpm(undefined);
        setAnswers({});
        setShowContinue(false); // ✅ ADD THIS
        setNeedsUserPlay(false);
        setIsPlaying(false);
        setAssessmentStep(0);
        setAssessment({
            anxiety: null,
            depression: null,
            sleep: null,
            stress: null,
        });
        onClose();
    };

    const pulseValue = Number(pulseCount);
    const isValidPulse =
        pulseCount !== "" && pulseValue >= 10 && pulseValue <= 50;

    const [assessmentStep, setAssessmentStep] = useState(0); // 0–3

    const [assessment, setAssessment] = useState({
        anxiety: null,
        depression: null,
        sleep: null,
        stress: null,
    });

    const audioRef = useRef();

    useEffect(() => {
        if (step === 4 && audioRef.current) {
            setNeedsUserPlay(false);
            audioRef.current.play()
                .then(() => setNeedsUserPlay(false))
                .catch(err => {
                    console.log("Audio play blocked", err);
                    setNeedsUserPlay(true);
                });
        }
    }, [step, audioUrl]);

    const handleManualPlay = () => {
        if (!audioRef.current) return;
        audioRef.current.play()
            .then(() => setNeedsUserPlay(false))
            .catch(err => console.log("Manual play failed", err));
    };

    const handleReset = () => {
        setPulseCount("");
        setBpm(undefined);
    };

    // Calculate wellness score from questionnaire (4-16 range)
    const calculateWellnessScore = () => {
        const { anxiety, depression, sleep, stress } = assessment;
        if (!anxiety || !depression || !sleep || !stress) return null;
        return anxiety + depression + sleep + stress;
    };

    // Get wellness index category based on score (4-16)
    const getWellnessIndex = (score) => {
        if (!score) return null;
        if (score >= 4 && score <= 8) return "low";      // Low stress
        if (score >= 9 && score <= 13) return "medium";  // Medium stress
        if (score >= 14 && score <= 16) return "high";   // High stress
        return null;
    };

    // Get heart rate category
    const getHeartRateCategory = (bpm) => {
        const value = Number(bpm);
        if (!value) return null;
        if (value >= 40 && value <= 75) return "low";
        if (value >= 76 && value <= 90) return "medium";
        if (value >= 91) return "high";
        return null;
    };

    // Combined stress level based on both heart rate and wellness score
    const getCombinedStressLevel = () => {
        const wellnessScore = calculateWellnessScore();
        const wellnessIndex = getWellnessIndex(wellnessScore);
        const heartRateCategory = getHeartRateCategory(bpm);

        if (!wellnessIndex || !heartRateCategory) return null;

        // Wellness Index vs Heart Rate matrix (from spreadsheet)
       const stressMatrix = {
            low: {
                low: "low",
                medium: "low",
                high: "medium"
            },
            medium: {
                low: "medium",   // fixed — was "low"
                medium: "medium",
                high: "high"      // fixed — was "medium"
            },
            high: {
                low: "medium",
                medium: "high",
                high: "high"
            }
        };

        return stressMatrix[wellnessIndex]?.[heartRateCategory] || "medium";
    };

    const getStressLevel = (bpm) => {
        const value = Number(bpm);
        if (!value) return null;

        if (value >= 40 && value <= 75) return "low";
        if (value >= 76 && value <= 90) return "medium";
        if (value > 90) return "high";

        return null;
    };

    const getRandomTracks = (tracks, count = 3) => {
        if (!tracks || !Array.isArray(tracks)) return [];
        return [...tracks]
            .sort(() => 0.5 - Math.random())
            .slice(0, count);
    };

    // Generate recommendations once the inputs (bpm + assessment) are complete.
    // Driven by bpm/assessment (NOT step) so navigating back to the list keeps
    // the same recommendations instead of reshuffling.
    useEffect(() => {
        const combinedLevel = getCombinedStressLevel();
        if (!combinedLevel) return;

        const tracksForLevel = soundTherapyTracks[combinedLevel] || [];
        // const selected = getRandomTracks(tracksForLevel, 3);

        // setSelectedTracks(selected);

        const selected = getRandomTracks(tracksForLevel, 3);
        const finalTracks =
            user?.account_id === 5
                ? selected.map(track => ({
                    ...track,
                    trackId: `consero-${track.trackId}`,
                }))
                : selected;
        setSelectedTracks(finalTracks);

        console.log("Heart Rate:", bpm, "BPM");
        console.log("Wellness Score:", calculateWellnessScore());
        console.log("Combined Stress Level:", combinedLevel);
        console.log("Selected Music Tracks:", selected);
    }, [bpm, assessment]);

    const formatTime = (seconds = 0) => {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    return (
        <>
            <Dialog
                open={open}
                maxWidth="sm"
                fullWidth
                onClose={(event, reason) => {
                    // ❌ Block backdrop click & ESC
                    if (reason === "backdropClick" || reason === "escapeKeyDown") {
                        return;
                    }
                }}
            >
                {/* Header */}
                <DialogTitle>
                    <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{
                            display: 'inline-block',
                            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            color: 'transparent', // 🔑 important
                        }}
                    >
                        {mode === 'biometric' ? '⌚ Biometric Assessment' : '📝 Manual Assessment'}
                    </Typography>
                    {/* {(step !== 1 && step !== 4) && ( */}
                    {step !== 4 && (
                        <IconButton
                            onClick={handleClose}
                            sx={{ position: "absolute", right: 8, top: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}

                    {step === 4 && (
                        <Button
                            variant="text"
                            size="small"
                            sx={{ position: "absolute", right: 8, top: 8, mt: 1, color: "#7843f3" }}
                            onClick={() => setStep(3)}
                        >
                            Back
                        </Button>
                    )}
                </DialogTitle>

                <DialogContent>

                    {/* STEP 1 – Manual Pulse Entry (Default Method) */}
                    {step === 1 && (
                        <Box>
                            {mode === 'biometric' ? (
                                <>
                                    {/* Biometric: Direct BPM Entry */}
                                    <Typography fontWeight={600} sx={{ mb: 0.5, color: "#7843f3" }}>
                                        Biometric Entry
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Enter your heart rate (BPM) from your device:
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={bpm || ''}
                                        onChange={(e) => {
                                            const val = Number(e.target.value);
                                            setBpm(val > 0 ? val : undefined);
                                        }}
                                        placeholder="e.g. 72"
                                        inputProps={{ min: 40, max: 200 }}
                                        error={bpm !== undefined && bpm !== null && (bpm < 40 || bpm > 200)}
                                    />

                                    {bpm !== undefined && bpm !== null && (bpm < 40 || bpm > 200) && (
                                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                            Please enter a value between 40–200 BPM
                                        </Typography>
                                    )}

                                    {bpm >= 40 && bpm <= 200 && (
                                        <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>
                                            Your heart rate: {bpm} BPM
                                        </Typography>
                                    )}

                                    <Button
                                        variant="contained"
                                        sx={{
                                            mt: 3,
                                            mx: 'auto',
                                            display: 'block',
                                            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                        }}
                                        disabled={!bpm || bpm < 40 || bpm > 200}
                                        onClick={() => setStep(2)}
                                    >
                                        Continue to Assessment
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {/* Manual: Pulse Count Entry */}
                                    <Typography fontWeight={600} sx={{ mb: 0.5, color: "#7843f3" }}>
                                        Manual Pulse Entry
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Enter your pulse count (15 seconds):
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={pulseCount}
                                        onChange={(e) => {
                                            setPulseCount(e.target.value);
                                            setBpm(undefined);
                                        }}
                                        placeholder="e.g. 18"
                                        inputProps={{ min: 10, max: 50 }}
                                        error={pulseCount !== "" && !isValidPulse}
                                    />

                                    {!isValidPulse && pulseCount !== "" && (
                                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                            Please enter a value between 10–50 beats
                                        </Typography>
                                    )}

                                    {bpm && (
                                        <Box sx={{
                                            mt: 2, p: 1.5, borderRadius: 2,
                                            background: 'linear-gradient(135deg, #f0fdf4, #f5f3ff)',
                                            border: '1px solid #d1fae5',
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" fontSize={12}>
                                                        {pulseCount} beats × 4 = {bpm} BPM
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#22c55e' }}>
                                                        Your heart rate: {bpm} BPM
                                                    </Typography>
                                                </Box>
                                                <Button variant="outlined" size="small" sx={{ color: "#7843f3" }} onClick={handleReset}>
                                                    Reset
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}

                                    <Button
                                        variant="contained"
                                        sx={{
                                            mt: 3,
                                            mx: 'auto',
                                            display: 'block',
                                            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                        }}
                                        disabled={!bpm && !isValidPulse}
                                        onClick={() => {
                                            if (!bpm) {
                                                const calculatedBpm = pulseValue * 4;
                                                setBpm(calculatedBpm);
                                                setShowContinue(true);
                                            } else {
                                                setStep(2);
                                            }
                                        }}
                                    >
                                        {bpm ? 'Continue to Assessment' : 'Calculate Heart Rate'}
                                    </Button>
                                </>)
                            }


                            {/* How to Measure Box - only for manual mode */}
                            {mode !== 'biometric' && <Box
                                sx={{
                                    mt: 3,
                                    p: 2,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 2,
                                    bgcolor: "#fafafa"
                                }}
                            >
                                <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                                    📋 How to Measure:
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Method : Manual Pulse Count</strong>
                                    <br />
                                    1. Find your pulse on your wrist (radial artery) or neck (carotid artery)
                                    <br />
                                    2. Use a timer and count beats for exactly 15 seconds
                                    <br />
                                    3. Enter the number of beats you counted above
                                    <br />
                                    4. We'll calculate your BPM (beats × 4)
                                </Typography>

                                {/* <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Method 2: Pulse Oximeter</strong>
                                    <br />
                                    If you have a pulse oximeter or fitness device, simply:
                                    <br />
                                    1. Take your heart rate reading from the device
                                    <br />
                                    2. Divide the BPM by 4 (e.g., 72 BPM ÷ 4 = 18)
                                    <br />
                                    3. Enter that number in the box above
                                </Typography> */}

                                <Typography variant="body2">
                                    <strong>Example:</strong> 18 beats in 15 seconds = 72 BPM
                                </Typography>
                            </Box>}

                            {/* {showContinue && (
                            <Button
                                fullWidth
                                variant="outlined"
                                sx={{ mt: 2 }}
                                onClick={() => setStep(2)}
                            >
                                Continue to Assessment
                            </Button>
                        )} */}
                        </Box>
                    )}


                    {/* STEP 2 – Wellness Assessment */}
                    {step === 2 && (
                        <Box>
                            <Typography fontWeight={600} mb={2} sx={{ color: "#7843f3" }}>
                                Wellness Assessment
                            </Typography>

                            {/* QUESTIONS CONFIG */}
                            {[
                                {
                                    key: "anxiety",
                                    question: "How would you describe your anxiety level today?",
                                    options: [
                                        { value: 1, emoji: "😌", label: "Calm & Peaceful" },
                                        { value: 2, emoji: "😐", label: "Slightly Anxious" },
                                        { value: 3, emoji: "😟", label: "Moderately Anxious" },
                                        { value: 4, emoji: "😰", label: "Highly Anxious" },
                                    ],
                                },
                                {
                                    key: "depression",
                                    question: "How has your mood been?",
                                    options: [
                                        { value: 1, emoji: "😊", label: "Happy & Energetic" },
                                        { value: 2, emoji: "😕", label: "Slightly Down" },
                                        { value: 3, emoji: "😔", label: "Moderately Low" },
                                        { value: 4, emoji: "😢", label: "Very Low" },
                                    ],
                                },
                                {
                                    key: "sleep",
                                    question: "Sleep quality recently?",
                                    options: [
                                        { value: 1, emoji: "😴", label: "Excellent" },
                                        { value: 2, emoji: "😪", label: "Fair" },
                                        { value: 3, emoji: "🥱", label: "Poor" },
                                        { value: 4, emoji: "😫", label: "Very Poor" },
                                    ],
                                },
                                {
                                    key: "stress",
                                    question: "Current stress level?",
                                    options: [
                                        { value: 1, emoji: "🧘", label: "Minimal" },
                                        { value: 2, emoji: "😅", label: "Moderate" },
                                        { value: 3, emoji: "😓", label: "High" },
                                        { value: 4, emoji: "🤯", label: "Extreme" },
                                    ],
                                },
                            ].map((q, index) =>
                                assessmentStep === index ? (
                                    <Box key={q.key}>
                                        {/* Question */}
                                        <Typography fontWeight={600} mb={2}>
                                            {q.question}
                                        </Typography>

                                        {/* Options */}
                                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                            {q.options.map((opt) => (
                                                <Grid
                                                    item
                                                    key={opt.value}
                                                    xs={12}        // 📱 Mobile → full width
                                                    sm={6}         // 💻 Tablet+ → 2 columns
                                                >
                                                    <Button
                                                        fullWidth
                                                        variant={assessment[q.key] === opt.value ? "contained" : "outlined"}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: {
                                                                xs: 'center',   // 📱 center on mobile
                                                                sm: 'flex-start',
                                                            },
                                                            gap: 1,
                                                            py: {
                                                                xs: 1.2,
                                                                sm: 1.5,
                                                            },
                                                            px: {
                                                                xs: 1.5,
                                                                sm: 2,
                                                            },
                                                            fontWeight: 600,
                                                            fontSize: {
                                                                xs: '0.85rem',
                                                                sm: '0.95rem',
                                                            },

                                                            // 🎨 base color
                                                            color: assessment[q.key] === opt.value ? '#fff' : '#7843f3',
                                                            borderColor: '#7843f3',

                                                            // 🎯 selected state
                                                            backgroundColor:
                                                                assessment[q.key] === opt.value ? '#7843f3' : 'transparent',

                                                            // 🖱 hover
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    assessment[q.key] === opt.value
                                                                        ? '#6a38e0'
                                                                        : 'rgba(120, 67, 243, 0.08)',
                                                                borderColor: '#7843f3',
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            setAssessment((prev) => ({
                                                                ...prev,
                                                                [q.key]: opt.value,
                                                            }))
                                                        }
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                fontSize: { xs: 18, sm: 20 },
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            {opt.emoji}
                                                        </Box>

                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                whiteSpace: 'normal',
                                                                textAlign: {
                                                                    xs: 'center',
                                                                    sm: 'left',
                                                                },
                                                            }}
                                                        >
                                                            {opt.label}
                                                        </Box>
                                                    </Button>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        {/* Next Button */}
                                        <Button
                                            sx={{
                                                mt: 3,
                                                mx: 'auto',          // 👈 centers horizontally
                                                display: 'block',    // required for mx auto
                                                background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                                color: assessment[q.key] ? '#fff' : '#9e9e9e', // 👈 text color
                                                '&.Mui-disabled': {
                                                    color: '#9e9e9e', // disabled text color
                                                },
                                            }}
                                            disabled={!assessment[q.key]}
                                            onClick={() => {
                                                if (assessmentStep < 3) {
                                                    setAssessmentStep((prev) => prev + 1);
                                                } else {
                                                    setStep(3); // move to Wellness Profile
                                                }
                                            }}
                                        >
                                            {assessmentStep === 3
                                                ? "Get Recommendations →"
                                                : "Next →"}
                                        </Button>
                                    </Box>
                                ) : null
                            )}
                        </Box>
                    )}

                    {/* STEP 3 – Your Wellness Profile */}
                    {step === 3 && (
                        <Box>
                            <Box
                                sx={{
                                    background: "linear-gradient(135deg, #f0f4ff 0%, #e6f2ff 100%)",
                                    borderRadius: 3,
                                    p: { xs: 2.5, sm: 3.5 },
                                    mb: 3
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                                    <Box
                                        sx={{
                                            width: { xs: 48, sm: 56 },
                                            height: { xs: 48, sm: 56 },
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: { xs: 24, sm: 28 },
                                            color: "white",
                                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)"
                                        }}
                                    >
                                        🧠
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" sx={{
                                            color: "#7843f3",
                                            fontWeight: 700,
                                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                        }}>
                                            Your Wellness Profile
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: "text.secondary",
                                            fontSize: { xs: '0.875rem', sm: '0.95rem' },
                                            mt: 0.5
                                        }}>
                                            AI Analysis Complete ✓
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* First Row: Heart Rate and Wellness Score */}
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                                        gap: { xs: 2.5, sm: 3 },
                                        mb: { xs: 2.5, sm: 3 }
                                    }}
                                >
                                    {/* Heart Rate Card */}
                                    <Box
                                        sx={{
                                            background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
                                            borderRadius: 3,
                                            p: { xs: 3.5, sm: 4 },
                                            textAlign: "center",
                                            boxShadow: "0 8px 24px rgba(120, 67, 243, 0.15)",
                                            border: "2px solid rgba(120, 67, 243, 0.1)",
                                            transition: "all 0.3s ease",
                                            position: "relative",
                                            overflow: "hidden",
                                            "&:hover": {
                                                transform: "translateY(-4px) scale(1.02)",
                                                boxShadow: "0 12px 32px rgba(120, 67, 243, 0.25)",
                                                borderColor: "rgba(120, 67, 243, 0.3)"
                                            },
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: "4px",
                                                background: "linear-gradient(90deg, #7843f3, #a78bfa)",
                                            }
                                        }}
                                    >
                                        <Typography variant="caption" sx={{
                                            color: "#7843f3",
                                            textTransform: "uppercase",
                                            letterSpacing: 1.2,
                                            fontWeight: 700,
                                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                            display: "block",
                                            mb: 1.5
                                        }}>
                                            HEART RATE
                                        </Typography>
                                        <Typography variant="h2" sx={{
                                            fontWeight: 800,
                                            background: "linear-gradient(135deg, #7843f3 0%, #a78bfa 100%)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            fontSize: { xs: '2rem', sm: '2.25rem' },
                                            lineHeight: 1,
                                            mb: 0.5
                                        }}>
                                            {bpm}
                                        </Typography>
                                        <Typography variant="subtitle1" sx={{
                                            color: "#7843f3",
                                            fontWeight: 700,
                                            fontSize: { xs: '0.875rem', sm: '0.95rem' },
                                            letterSpacing: 0.5
                                        }}>
                                            BPM
                                        </Typography>
                                    </Box>

                                    {/* Wellness Score Card */}
                                    <Box
                                        sx={{
                                            background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                                            borderRadius: 3,
                                            p: { xs: 3.5, sm: 4 },
                                            textAlign: "center",
                                            boxShadow: "0 8px 24px rgba(34, 197, 94, 0.15)",
                                            border: "2px solid rgba(34, 197, 94, 0.1)",
                                            transition: "all 0.3s ease",
                                            position: "relative",
                                            overflow: "hidden",
                                            "&:hover": {
                                                transform: "translateY(-4px) scale(1.02)",
                                                boxShadow: "0 12px 32px rgba(34, 197, 94, 0.25)",
                                                borderColor: "rgba(34, 197, 94, 0.3)"
                                            },
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: "4px",
                                                background: "linear-gradient(90deg, #22c55e, #10b981)",
                                            }
                                        }}
                                    >
                                        <Typography variant="caption" sx={{
                                            color: "#22c55e",
                                            textTransform: "uppercase",
                                            letterSpacing: 1.2,
                                            fontWeight: 700,
                                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                            display: "block",
                                            mb: 1.5
                                        }}>
                                            COMPOSITE WELLNESS SCORE
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.5 }}>
                                            <Typography variant="h2" sx={{
                                                fontWeight: 800,
                                                background: "linear-gradient(135deg, #22c55e 0%, #10b981 100%)",
                                                WebkitBackgroundClip: "text",
                                                WebkitTextFillColor: "transparent",
                                                fontSize: { xs: '2rem', sm: '2.25rem' },
                                                lineHeight: 1
                                            }}>
                                                {calculateWellnessScore() || "--"}
                                            </Typography>
                                            <Typography variant="h5" sx={{
                                                color: "#22c55e",
                                                fontWeight: 700,
                                                opacity: 0.7,
                                                fontSize: { xs: '1rem', sm: '1.1rem' }
                                            }}>
                                                /16
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Second Row: Stress Level (Full Width) */}
                                <Box sx={{ mb: 3.5 }}>
                                    <Box
                                        sx={{
                                            background: getCombinedStressLevel() === "low"
                                                ? "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)"
                                                : getCombinedStressLevel() === "medium"
                                                    ? "linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)"
                                                    : "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
                                            borderRadius: 3,
                                            p: { xs: 3.5, sm: 4 },
                                            textAlign: "center",
                                            boxShadow: getCombinedStressLevel() === "low"
                                                ? "0 8px 24px rgba(34, 197, 94, 0.15)"
                                                : getCombinedStressLevel() === "medium"
                                                    ? "0 8px 24px rgba(245, 158, 11, 0.15)"
                                                    : "0 8px 24px rgba(239, 68, 68, 0.15)",
                                            border: getCombinedStressLevel() === "low"
                                                ? "2px solid rgba(34, 197, 94, 0.1)"
                                                : getCombinedStressLevel() === "medium"
                                                    ? "2px solid rgba(245, 158, 11, 0.1)"
                                                    : "2px solid rgba(239, 68, 68, 0.1)",
                                            transition: "all 0.3s ease",
                                            position: "relative",
                                            overflow: "hidden",
                                            "&:hover": {
                                                transform: "translateY(-4px) scale(1.02)",
                                                boxShadow: getCombinedStressLevel() === "low"
                                                    ? "0 12px 32px rgba(34, 197, 94, 0.25)"
                                                    : getCombinedStressLevel() === "medium"
                                                        ? "0 12px 32px rgba(245, 158, 11, 0.25)"
                                                        : "0 12px 32px rgba(239, 68, 68, 0.25)",
                                                borderColor: getCombinedStressLevel() === "low"
                                                    ? "rgba(34, 197, 94, 0.3)"
                                                    : getCombinedStressLevel() === "medium"
                                                        ? "rgba(245, 158, 11, 0.3)"
                                                        : "rgba(239, 68, 68, 0.3)"
                                            },
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: "4px",
                                                background: getCombinedStressLevel() === "low"
                                                    ? "linear-gradient(90deg, #22c55e, #10b981)"
                                                    : getCombinedStressLevel() === "medium"
                                                        ? "linear-gradient(90deg, #f59e0b, #f97316)"
                                                        : "linear-gradient(90deg, #ef4444, #dc2626)",
                                            }
                                        }}
                                    >
                                        <Typography variant="caption" sx={{
                                            color: getCombinedStressLevel() === "low" ? "#22c55e" :
                                                getCombinedStressLevel() === "medium" ? "#f59e0b" : "#ef4444",
                                            textTransform: "uppercase",
                                            letterSpacing: 1.2,
                                            fontWeight: 700,
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            display: "block",
                                            mb: 1.5
                                        }}>
                                            STRESS LEVEL
                                        </Typography>
                                        <Typography variant="h3" sx={{
                                            fontWeight: 800,
                                            background: getCombinedStressLevel() === "low"
                                                ? "linear-gradient(135deg, #22c55e 0%, #10b981 100%)"
                                                : getCombinedStressLevel() === "medium"
                                                    ? "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)"
                                                    : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            fontSize: { xs: '2rem', sm: '2.5rem' },
                                            lineHeight: 1.2,
                                            textTransform: "capitalize"
                                        }}>
                                            {getCombinedStressLevel() || "--"}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        background: "white",
                                        borderRadius: 2.5,
                                        p: { xs: 2.5, sm: 3 },
                                        borderLeft: "5px solid #667eea",
                                        boxShadow: "0 2px 12px rgba(0,0,0,0.06)"
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{
                                        mb: 1.5,
                                        fontWeight: 700,
                                        fontSize: { xs: '1rem', sm: '1.1rem' },
                                        color: "#2d3748"
                                    }}>
                                        💡 echo recommendation
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: "text.secondary",
                                        lineHeight: 1.7,
                                        fontSize: { xs: '0.875rem', sm: '0.95rem' }
                                    }}>
                                        Based on your heart rate: <strong> {getHeartRateCategory()}BPM</strong> and wellness score
                                        : <strong>{calculateWellnessScore()}/16</strong>, your combined stress level is{" "}
                                        <strong style={{
                                            color: getCombinedStressLevel() === "low" ? "#22c55e" :
                                                getCombinedStressLevel() === "medium" ? "#f59e0b" : "#ef4444"
                                        }}>
                                            {getCombinedStressLevel()}
                                        </strong>. We recommend personalized therapeutic
                                        music sessions to help you relax and restore balance.
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Personalized Tracks */}
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        mb: 1,
                                        fontWeight: 600,
                                        display: 'inline-block',
                                        background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        color: 'transparent', // 🔑 important
                                    }}
                                >
                                    🎵 Your Personalized Tracks
                                </Typography>

                                {/* Description */}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        color: "#4a5568",
                                        fontSize: "0.875rem",   // smaller text
                                        lineHeight: 2,          // more gap between lines
                                        maxWidth: 700
                                    }}
                                >
                                    Choose any track that resonates with you.
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        color: "#4a5568",
                                        fontSize: "0.875rem",   // smaller text
                                        lineHeight: 2,          // more gap between lines
                                        maxWidth: 700
                                    }}
                                >
                                    Put on your headphones, find a calm and comfortable place,
                                    and allow yourself 30 minutes of uninterrupted listening.
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        color: "#4a5568",
                                        fontSize: "0.875rem",   // smaller text
                                        lineHeight: 2,          // more gap between lines
                                        maxWidth: 700
                                    }}
                                >
                                    Let the sounds gently guide you into a state of deep relaxation,
                                    soothing calm, and inner balance.
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        color: "#4a5568",
                                        fontSize: "0.875rem",   // smaller text
                                        lineHeight: 2,          // more gap between lines
                                        maxWidth: 700
                                    }}
                                >
                                    Simply listen. Simply breathe.
                                </Typography>


                                <Box
                                    sx={{
                                        display: "grid",
                                        gap: 2
                                    }}
                                >
                                    {selectedTracks.map((track, i) => {
                                        const isSelected = selectedTrack?.title === track.title;

                                        const IconComponent =
                                            Icons[track.icon] || Icons.MusicNote;

                                        return (
                                            <Box
                                                key={i}
                                                sx={{
                                                    background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', // ✅ gradient background
                                                    border: `2px solid ${isSelected ? '#7843f3' : '#e2e8f0'}`,
                                                    borderRadius: 2,
                                                    p: 2,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: isSelected
                                                        ? '0 8px 20px rgba(120,67,243,0.25)'
                                                        : '0 2px 8px rgba(0,0,0,0.05)',
                                                    '&:hover': {
                                                        transform: 'translateX(5px)',
                                                        borderColor: '#7843f3',
                                                    },
                                                }}
                                                onClick={() => setSelectedTrack(track)}
                                            >
                                                {/* Top Row */}
                                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                    <IconComponent
                                                        fontSize="large"
                                                        sx={{ color: "#667eea" }}   // blue
                                                    />

                                                    {/* <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: "white",
                                                            background: "#667eea",
                                                            px: 1.5,
                                                            height: 26,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            borderRadius: 2,
                                                            whiteSpace: "nowrap"
                                                        }}
                                                    >
                                                        {Math.floor(track.duration / 60)} min
                                                    </Typography> */}
                                                </Box>

                                                {/* Title */}
                                                <Typography
                                                    sx={{ fontSize: 18, fontWeight: 600, color: "#2d3748", mb: 0.5 }}
                                                >
                                                    {track.title}
                                                </Typography>

                                                {/* Description */}
                                                <Typography sx={{ fontSize: 14, color: "#718096" }}>
                                                    {track.subtitle1}
                                                </Typography>

                                                {/* Tag */}
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: "#667eea",
                                                            display: "inline-block",
                                                            background: "rgba(102,126,234,0.1)",
                                                            px: 1,
                                                            borderRadius: 1
                                                        }}
                                                    >
                                                        {track.subtitle2}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                sx={{
                                    mt: 3,
                                    mx: 'auto',          // 👈 centers horizontally
                                    display: 'block', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                }}
                                disabled={!selectedTrack || loading}
                                onClick={async () => {
                                    if (!selectedTrack?.trackId) return;

                                    try {
                                        setLoading(true);

                                        const data = await getSmartBiometricSound(selectedTrack.trackId);

                                        // store signed url
                                        setAudioUrl(data.track_url);

                                        // move to audio player
                                        setStep(4);
                                    } catch (err) {
                                        console.error(err);
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                            >
                                {loading ? "Loading..." : "Play Track →"}
                            </Button>

                        </Box>
                    )}

                    {/* STEP 4 – Therapy Music Player */}
                    {step === 4 && (
                        <Box className="audio-player">

                            <Box className="player-header" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 28,
                                        color: 'white',
                                    }}
                                >
                                    🎵
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ color: "#7843f3" }}>{selectedTrack?.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        echo Therapeutic Session
                                    </Typography>
                                </Box>
                            </Box>



                            <Box className="live-bio-display" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                                <Box className="live-bio-card" sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                                    <Typography className="live-bio-icon">❤️</Typography>
                                    <Typography className="live-bio-value" sx={{ color: "#7843f3" }}>{bpm || "--"}</Typography>
                                    <Typography className="live-bio-label" sx={{ color: "#7843f3" }}>Heart Rate</Typography>
                                </Box>
                                <Box
                                    className="live-bio-card"
                                    sx={{
                                        textAlign: 'center',
                                        p: 2,
                                        bgcolor: 'rgba(0,0,0,0.05)',
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography className="live-bio-icon">📊</Typography>

                                    <Typography className="live-bio-value" sx={{ color: "#7843f3", textTransform: "capitalize" }}>
                                        {getCombinedStressLevel() || "--"}
                                    </Typography>


                                    <Typography className="live-bio-label" sx={{ color: "#7843f3" }}>Stress</Typography>
                                </Box>

                            </Box>

                            <audio
                                ref={audioRef}
                                src={audioUrl}
                                autoPlay
                                preload="auto"
                                style={{ display: "none" }} // 🔥 hide native UI

                                onPlay={() => { setIsPlaying(true); setNeedsUserPlay(false); }}

                                onPause={() => setIsPlaying(false)}

                                // ⛔ block seeking
                                onSeeking={(e) => {
                                    e.target.currentTime = currentTime;
                                }}

                                onLoadedMetadata={(e) => {
                                    setDuration(e.target.duration);
                                }}

                                onTimeUpdate={(e) => {
                                    setCurrentTime(e.target.currentTime);
                                }}

                                onEnded={() => setIsAudioCompleted(true)}
                            />

                            <Box sx={{ mt: 2 }}>
                                {/* Progress info */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {formatTime(currentTime)}
                                    </Typography>

                                    <Typography variant="caption" color="text.secondary">
                                        {formatTime(duration)}
                                    </Typography>
                                </Box>

                                {/* Read-only progress bar */}
                                <Box
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: "#e5e7eb",
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: "100%",
                                            width: `${(currentTime / duration) * 100 || 0}%`,
                                            background: "linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)",
                                            transition: "width 0.25s linear",
                                        }}
                                    />
                                </Box>
                            </Box>

                            {!isPlaying && !isAudioCompleted && (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{
                                        mt: 3,
                                        py: 1.4,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                    }}
                                    onClick={handleManualPlay}
                                >
                                    {currentTime > 0 ? '▶ Resume' : '▶ Tap to Play'}
                                </Button>
                            )}

                            {isAudioCompleted && (<Button
                                variant="contained"
                                sx={{
                                    mt: 3,
                                    mx: 'auto',          // 👈 centers horizontally
                                    display: 'block', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                }}
                                onClick={() => setOpenCompleteDialog(true)}
                            >
                                Complete Session
                            </Button>)}
                        </Box>
                    )}


                </DialogContent>
            </Dialog>
            <Dialog
                open={openCompleteDialog}
                onClose={() => {
                    setOpenCompleteDialog(false);
                    handleClose();
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: "center", p: 4 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: "#7843f3" }}>
                        ✅ Session Completed
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Be with Echo for 30 minutes a day and build a healthy habit for managing stress.
                    </Typography>

                    <Button
                        variant="contained"
                        sx={{
                            mt: 3, mx: 'auto',          // 👈 centers horizontally
                            display: 'block', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                        }}
                        onClick={() => {
                            setOpenCompleteDialog(false);
                            handleClose();
                        }}
                    >
                        Done
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SmartBiometricTherapyDialog;
